// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { isDefined, isEmpty } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logStatus.i18n;

const statusesData = {
	completed: {
		label: strings.completedStatus,
		icon: 'check-circle-solid',
		colorVariable: '--uap-log-status-completed',
	},
	'completed-do-nothing': {
		label: strings.completedDoNothingStatus,
		icon: 'check-circle-solid',
		colorVariable: '--uap-log-status-completed-do-nothing',
	},
	'completed-did-nothing': {
		label: strings.completedDidNothingStatus,
		icon: 'check-circle-solid',
		colorVariable: '--uap-log-status-completed-did-nothing',
	},
	cancelled: {
		label: strings.cancelledStatus,
		icon: 'times-circle-solid',
		colorVariable: '--uap-log-status-cancelled',
	},
	paused: {
		label: strings.pausedStatus,
		icon: 'pause',
		colorVariable: '--uap-log-status-paused',
	},
	'completed-with-notice': {
		label: strings.completedWithNoticeStatus,
		icon: 'check-circle-solid',
		colorVariable: '--uap-log-status-completed-with-notice',
	},
	'skipped': {
		label: strings.skipped,
		icon: 'circle-minus-solid',
		colorVariable: '--uap-log-status-skipped',
	},
	'not-completed': {
		label: strings.notCompletedStatus,
		icon: 'circle-minus-solid',
		colorVariable: '--uap-log-status-not-completed',
	},
	queued: {
		label: strings.queuedStatus,
		icon: 'hourglass',
		colorVariable: '--uap-log-status-queued',
	},
	'completed-with-errors': {
		label: strings.completedWithErrorsStatus,
		icon: 'times-circle-solid',
		colorVariable: '--uap-log-status-completed-with-errors',
	},
	'in-progress': {
		label: strings.inProgressStatus,
		icon: 'clock-solid',
		colorVariable: '--uap-log-status-in-progress',
	},
	'in-progress-with-errors': {
		label: strings.inProgressWithErrorsStatus,
		icon: 'clock-solid',
		colorVariable: '--uap-log-status-in-progress-with-errors',
	},
	scheduled: {
		label: strings.scheduledStatus,
		icon: 'clock-solid',
		colorVariable: '--uap-log-status-scheduled',
	},
	delayed: {
		label: strings.delayedStatus,
		icon: 'clock-solid',
		colorVariable: '--uap-log-status-delayed',
	},
	failed: {
		label: strings.failedStatus,
		icon: 'times-circle-solid',
		colorVariable: '--uap-log-status-failed',
	},
	'completed-awaiting': {
		label: strings.completedAwaitingStatus,
		icon: 'clock-solid',
		colorVariable: '--uap-log-status-completed-awaiting',
	},
	'condition-met': {
		label: strings.conditionMetStatus,
		icon: 'check-circle-solid',
		colorVariable: '--uap-log-status-condition-met',
	},
	'condition-not-met': {
		label: strings.conditionNotMetStatus,
		icon: 'times-circle-solid',
		colorVariable: '--uap-log-status-condition-not-met',
	},
	'condition-not-evaluated': {
		label: strings.conditionNotEvaluatedStatus,
		icon: 'circle-minus-solid',
		colorVariable: '--uap-log-status-condition-not-evaluated',
	},
}

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-status' )
export class AutomatorLogStatus extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: String, attribute: 'status-id' } ) accessor statusID;

	@property( { type: Number, attribute: 'count-number' } ) accessor countNumber;

	@property( { type: Boolean, attribute: 'hide-label' } ) accessor hideLabel = false;

	@property( { type: Boolean, attribute: 'show-tooltip' } ) accessor showTooltip = false;

	// eslint-disable-next-line require-jsdoc
	render() {

		// Check that the user is using a valid ID
		if ( ! isDefined( statusesData[ this.statusID ] ) ) {
			return html`${ sprintf( strings.invalidStatus, this.statusID ) }`;
		}

		if ( ! this.showTooltip ) {
			return this._templateStatus;
		}

		return html`
			<uo-tooltip>
                <span class="tooltip-nowrap">${ statusesData[ this.statusID ].label }</span>

                <div slot="target">${ this._templateStatus }</div>
            </uo-tooltip>
			
		`;
	}

	/**
	 * Generates a template for the status component.
	 *
	 * @return {TemplateResult} - A lit-html template result for the status component. The template includes a status icon, a count number (if not empty), and a label (if not hidden).
	 */
	get _templateStatus() {
		return html`
			<div class="status">
				<span class="status__icon" style="color: var(${ statusesData[ this.statusID ].colorVariable })">
					<uo-icon id="${ statusesData[ this.statusID ].icon }"></uo-icon>
				</span>

				${
					! isEmpty( this.countNumber ) ?
					html`<span class="status__count">${ this.countNumber.toLocaleString() }</span>` : ''
				}

				${ ! this.hideLabel ? html`<span class="status__label">${ statusesData[ this.statusID ].label }</span>` : '' }
			</div>
		`
	}
}
